/**
 * Role constants and user access configuration
 * Ported from Angular frontend-app: src/app/basecomponent/layout/user-access.ts
 */

export const LIBERTY_ROLES = [
  'ROLE_ADMIN',
  'ROLE_EXECS',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
  'ROLE_LIBERTY_DEFAULTS',
  'ROLE_LIBERTY_SALES',
  'ROLE_UNDERWRITER',
  'ROLE_LIBERTY_FIN_MGR',
  'EXTERNAL_INSURANCE',
  'ROLE_LIBERTY_ACCOUNTING',
  'ROLE_REFUND_APPROVAL',
] as const

export const COMPANY_ROLES = [
  'ROLE_PROPERTY_MANAGER',
  'ROLE_PROPERTY_AGENT',
  'ROLE_PROPERTY_ADMIN',
  'ROLE_COMPANY_MANAGER',
  'ROLE_COMPANY_ADMIN',
  'ROLE_REGIONAL_MANAGER',
] as const

export const COMPANY_SPECIFIC_ROLES = [
  'ROLE_COMPANY_MANAGER',
  'ROLE_COMPANY_ADMIN',
  'ROLE_REGIONAL_MANAGER',
] as const

export const PROPERTY_ROLES = [
  'ROLE_PROPERTY_MANAGER',
  'ROLE_PROPERTY_AGENT',
  'ROLE_PROPERTY_ADMIN',
] as const

export const CHARITY_ROLES = [
  'ROLE_ATLANTA_OPEN_DOOR_ADMIN',
  'ROLE_ATLANTA_OPEN_DOOR_MANAGER',
] as const

/**
 * User access matrix - maps roles to feature permissions (Read/Write)
 * Ported from Angular user_access object
 */
export const USER_ACCESS: Record<string, Record<string, string[]>> = {
  ROLE_ADMIN: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    reports: ['Write'],
    email_templates: ['Write'],
    application: ['Write'],
    invite: ['Write'],
    resident: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Write'],
    housing_assist: ['Write'],
    jobs: ['Write'],
    mailtrap: ['Write'],
    default: ['Write'],
    download_application: [],
    underwriting_config: [],
  },
  ROLE_EXECS: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    invite: ['Write'],
    agent: ['Read'],
    reports: ['Read'],
    application: ['Write'],
    resident: ['Write'],
    atlanta_resident: ['Read'],
    atlanta_default: ['Read'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    default: ['Write'],
  },
  ROLE_CUSTOMER_SERVICE: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    reports: ['Read'],
    application: ['Write'],
    invite: ['Write'],
    resident: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    default: ['Write'],
  },
  ROLE_CUSTOMER_SERVICE_MANAGER: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    reports: ['Read'],
    application: ['Write'],
    invite: ['Write'],
    resident: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    default: ['Write'],
  },
  ROLE_REFUND_APPROVAL: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    reports: ['Read'],
    application: ['Write'],
    invite: ['Write'],
    resident: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    default: ['Write'],
  },
  ROLE_LIBERTY_DEFAULTS: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    reports: ['Read'],
    application: ['Write'],
    resident: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    default: ['Write'],
  },
  ROLE_LIBERTY_SALES: {
    company: ['Write'],
    property: ['Write'],
    invite: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    reports: ['Read'],
    application: ['Read'],
    resident: ['Read'],
    default: ['Write'],
  },
  ROLE_UNDERWRITER: {
    resident: ['Read'],
    default: ['Read'],
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    reports: ['Read'],
    application: ['Write'],
    underwriting_config: [],
  },
  EXTERNAL_INSURANCE: {
    default: ['Write'],
  },
  ROLE_ATLANTA_OPEN_DOOR_ADMIN: {
    company: ['Read'],
    property: ['Read'],
    user_management: ['Read'],
    agent: ['Write'],
    referring_agencies: ['Write'],
    housing_assist: ['Write'],
    reports: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
  },
  ROLE_ATLANTA_OPEN_DOOR_MANAGER: {
    company: ['Read'],
    property: ['Read'],
    user_management: ['Read'],
    agent: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    reports: ['Read'],
    atlanta_resident: ['Read'],
    atlanta_default: ['Read'],
  },
  ROLE_COMPANY_ADMIN: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    application: ['Read'],
    resident: ['Write'],
    default: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    reports: ['Read'],
  },
  ROLE_COMPANY_MANAGER: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    application: ['Read'],
    resident: ['Write'],
    default: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    reports: ['Read'],
  },
  ROLE_REGIONAL_MANAGER: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    application: ['Read'],
    invite: ['Write'],
    resident: ['Write'],
    default: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    reports: ['Read'],
  },
  ROLE_PROPERTY_ADMIN: {
    company: ['Read'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    application: ['Read'],
    invite: ['Write'],
    resident: ['Write'],
    default: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    reports: ['Read'],
  },
  ROLE_PROPERTY_AGENT: {
    company: ['Read'],
    property: ['Read'],
    agent: ['Read'],
    application: ['Read'],
    invite: ['Write'],
    reports: ['Read'],
    resident: ['Write'],
    default: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
  },
  ROLE_PROPERTY_MANAGER: {
    company: ['Read'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    application: ['Read'],
    invite: ['Write'],
    resident: ['Write'],
    default: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Read'],
    housing_assist: ['Read'],
    reports: ['Read'],
  },
  ROLE_RENTER: {
    resident_application: ['Write'],
  },
  ROLE_LIBERTY_FIN_MGR: {
    company: ['Read'],
    property: ['Read'],
    reports: ['Read'],
    application: ['Write'],
    default: ['Write'],
  },
  ROLE_LIBERTY_ACCOUNTING: {
    company: ['Write'],
    property: ['Write'],
    user_management: ['Write'],
    agent: ['Write'],
    reports: ['Write'],
    email_templates: ['Write'],
    application: ['Write'],
    invite: ['Write'],
    resident: ['Write'],
    atlanta_resident: ['Write'],
    atlanta_default: ['Write'],
    referring_agencies: ['Write'],
    housing_assist: ['Write'],
    default: ['Write'],
  },
}

/**
 * Human-readable display names for each role
 * Mirrors Angular's filter_user_roles mapping from the user-roles store
 */
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  ROLE_ADMIN: 'Liberty Admin',
  ROLE_EXECS: 'Liberty Execs',
  ROLE_CUSTOMER_SERVICE: 'Liberty Customer Service',
  ROLE_CUSTOMER_SERVICE_MANAGER: 'Liberty Customer Service Manager',
  ROLE_LIBERTY_DEFAULTS: 'Liberty Default',
  ROLE_LIBERTY_SALES: 'Liberty Sales',
  ROLE_UNDERWRITER: 'Liberty Underwriter',
  ROLE_LIBERTY_FIN_MGR: 'Liberty Finance Manager',
  ROLE_LIBERTY_ACCOUNTING: 'Liberty Accounting',
  ROLE_REFUND_APPROVAL: 'Liberty Refund Approval',
  EXTERNAL_INSURANCE: 'External Insurance',
  ROLE_COMPANY_ADMIN: 'Company Admin',
  ROLE_COMPANY_MANAGER: 'Company Manager',
  ROLE_REGIONAL_MANAGER: 'Company Regional Manager',
  ROLE_PROPERTY_ADMIN: 'Property Assistant',
  ROLE_PROPERTY_AGENT: 'Leasing Consultant',
  ROLE_PROPERTY_MANAGER: 'Property Manager',
  ROLE_RENTER: 'Renter',
  ROLE_ATLANTA_OPEN_DOOR_ADMIN: 'Atlanta Open Door Admin',
  ROLE_ATLANTA_OPEN_DOOR_MANAGER: 'Atlanta Open Door Manager',
}

/**
 * Get human-readable display name for a role
 * Falls back to formatting the role_name if not found in the mapping
 */
export function getRoleDisplayName(roleName: string | undefined | null): string {
  if (!roleName) return 'Unknown Role'
  if (ROLE_DISPLAY_NAMES[roleName]) return ROLE_DISPLAY_NAMES[roleName]
  // Fallback: convert ROLE_SOME_NAME to Some Name
  return roleName
    .replace(/^ROLE_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Get initials for a role display name (for collapsed sidebar avatar)
 */
export function getRoleInitials(roleName: string | undefined | null): string {
  const displayName = getRoleDisplayName(roleName)
  const words = displayName.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }
  return displayName.substring(0, 2).toUpperCase()
}

export type LibertyRole = (typeof LIBERTY_ROLES)[number]
export type CompanyRole = (typeof COMPANY_ROLES)[number]
export type PropertyRole = (typeof PROPERTY_ROLES)[number]
export type CharityRole = (typeof CHARITY_ROLES)[number]
export type RoleName = string
